import React from "react";

import { colors } from "theme/colors";
import { getColor } from "containers/RiskAssessmentsDetails/RiskMatrix/styles";

import {
  StyledLikelihoodText,
  StyledImpactText,
  StyledLikelihoodCell,
  StyledRiskMatrix,
  StyledMatrixContainer,
  StyledMatrixWrapper,
} from "./styles";
import { ConfigurableRiskMatrixTableProps } from "./types";
import { generateScores, getDynamicRiskLabel } from "./helpers";
import { Box } from "@mui/material";

export const ConfigurableRiskMatrixTable: React.FC<ConfigurableRiskMatrixTableProps> = ({
  selected,
  onChange,
  matrixRows,
  matrixColumns,
  impactLabels,
  likelihoodLabels,
  readOnly = false,
}) => {
  const scores = generateScores(matrixRows, matrixColumns);

  const impactLabelsArray: string[] = Array.isArray(impactLabels)
    ? impactLabels
    : impactLabels.split(",");
  const likelihoodLabelsArray: string[] = Array.isArray(likelihoodLabels)
    ? likelihoodLabels
    : likelihoodLabels.split(",");

  return (
    <StyledMatrixContainer>
      <StyledMatrixWrapper>
        <StyledLikelihoodText>Likelihood</StyledLikelihoodText>
        <StyledRiskMatrix>
          {scores.map((row, rowIndex) => (
            <Box
              key={rowIndex}
              sx={{ display: "flex", borderBottom: `1px solid ${colors.grey[400]}` }}
            >
              <StyledLikelihoodCell>
                {likelihoodLabelsArray[rowIndex] || `Level ${rowIndex + 1}`}
              </StyledLikelihoodCell>
              <StyledLikelihoodCell style={{ width: "30px" }}>
                {matrixRows - rowIndex}
              </StyledLikelihoodCell>
              {row.map((score, colIndex) => {
                const impact = matrixRows - rowIndex;
                const likelihood = colIndex + 1;
                const { bg, color } = getColor(impact, likelihood, matrixRows, matrixColumns);

                return (
                  <StyledLikelihoodCell
                    key={colIndex}
                    style={{
                      color,
                      cursor: readOnly ? "default" : "pointer",
                      backgroundColor: bg,
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={readOnly ? undefined : () => onChange({ impact, likelihood, risk: impact * likelihood })}
                  >
                    {!readOnly && (
                      <input
                        type="radio"
                        style={{ padding: "2px", marginRight: "4px" }}
                        checked={selected.impact === impact && selected.likelihood === likelihood}
                        onChange={() => onChange({ impact, likelihood, risk: impact * likelihood })}
                      />
                    )}
                    <p style={{ margin: 0 }}>
                      {score} - {getDynamicRiskLabel(impact, likelihood, matrixRows, matrixColumns)}
                    </p>
                  </StyledLikelihoodCell>
                );
              })}
            </Box>
          ))}

          <Box sx={{ display: "flex" }}>
            <StyledLikelihoodCell />
            <StyledLikelihoodCell style={{ width: "30px" }} />
            {impactLabelsArray.map((impact, i) => (
              <StyledLikelihoodCell
                key={i}
                style={{ borderBottom: `1px solid ${colors.grey[400]}` }}
              >
                {impact}
              </StyledLikelihoodCell>
            ))}
          </Box>
          <Box sx={{ display: "flex" }}>
            <StyledLikelihoodCell style={{ height: "30px" }} />
            <StyledLikelihoodCell style={{ height: "30px", width: "30px" }} />
            {[...Array(matrixColumns)].map((_, colIndex) => (
              <StyledLikelihoodCell key={colIndex} style={{ height: "30px" }}>
                {colIndex + 1}
              </StyledLikelihoodCell>
            ))}
          </Box>
        </StyledRiskMatrix>
        <StyledImpactText>Impact</StyledImpactText>
      </StyledMatrixWrapper>
    </StyledMatrixContainer>
  );
};
